import type { ScanPartHistoryRecord } from '@kaiizen/kai-pro-api';
import {
  getOptimizedTask,
  getScanPartFromScanDataInMemoryWithoutChildren,
  getTotalCostIfDefined,
  type ScanTask,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

/** Each part's version as of `at`: the newest at or before it, leaving out any part whose version then was a deletion. */
const getVersionsAt = (scanPartHistory: readonly ScanPartHistoryRecord[], at: Date) => {
  const versionsByScanPartId = new Map<string, ScanPartHistoryRecord>();
  const seenScanPartIds = new Set<string>();
  for (const record of scanPartHistory) {
    if (record.updatedAt > at.getTime() || seenScanPartIds.has(record.scanPartId)) continue;
    seenScanPartIds.add(record.scanPartId);
    if (!record.deleted) versionsByScanPartId.set(record.scanPartId, record);
  }
  return versionsByScanPartId;
};

/**
 * The task as it stood at `at`, rebuilt from its history: its version then, the options its children
 * listed then, and those options' line items then.
 */
const getTaskAt = (taskScanPartId: string, scanPartHistory: readonly ScanPartHistoryRecord[], at: Date): ScanTask | undefined => {
  const versionsByScanPartId = getVersionsAt(scanPartHistory, at);
  const getChildren = (record: ScanPartHistoryRecord) =>
    (record.childrenScanPartIds ?? []).flatMap(childScanPartId => versionsByScanPartId.get(childScanPartId) ?? []);
  const taskRecord = versionsByScanPartId.get(taskScanPartId);
  if (!taskRecord) return undefined;
  return {
    ...getScanPartFromScanDataInMemoryWithoutChildren(taskRecord),
    options: getChildren(taskRecord).map(optionRecord => ({
      ...getScanPartFromScanDataInMemoryWithoutChildren(optionRecord),
      lineItems: getChildren(optionRecord).map(getScanPartFromScanDataInMemoryWithoutChildren),
    })),
  } as ScanTask;
};

/**
 * What the task's total cost was at `at`, computed as the scope editor computes it now: the task rebuilt
 * from its history, priced through its current template, within its group's current values (so
 * measurements read as they stand today). Undefined when the task didn't exist yet, or a part's
 * template is gone and it can't be priced.
 *
 * @param task - the task as it is now: its id, template and group values
 * @param scanPartHistory - the task's history (every version of it and its descendants), newest first
 */
export const getTaskTotalCostAt = ({ task, scanPartHistory, at }: {
  task: ScanTaskOptimized;
  scanPartHistory: readonly ScanPartHistoryRecord[];
  at: Date;
}): number | undefined => {
  const taskAt = getTaskAt(task.id, scanPartHistory, at);
  if (!taskAt || !task.template) return undefined;
  try {
    return getTotalCostIfDefined(getOptimizedTask(taskAt, task.template, task.optimized.optimizedValueExpressions.scope));
  } catch {
    return undefined;
  }
};
